import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./consent.css";
import * as CookieConsent from "vanilla-cookieconsent";

interface TiktokPixel {
  grantConsent: () => void;
  revokeConsent: () => void;
  page: (...args: unknown[]) => void;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    oaiq?: (...args: unknown[]) => void;
    ttq?: TiktokPixel;
    openCookiePreferences?: () => void;
  }
}

let openaiPageViewSent = false;
let tiktokPageViewSent = false;

// Map the user's category choices onto the advertising/analytics platforms.
function applyConsent(): void {
  const analytics = CookieConsent.acceptedCategory("analytics");
  const ads = CookieConsent.acceptedCategory("ads");

  // Google Consent Mode v2
  window.gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: ads ? "granted" : "denied",
    ad_user_data: ads ? "granted" : "denied",
    ad_personalization: ads ? "granted" : "denied",
  });

  // Meta Pixel (Google Consent Mode does not cover Meta — gate it directly).
  window.fbq?.("consent", ads ? "grant" : "revoke");

  window.oaiq?.("consent", ads);
  if (ads && !openaiPageViewSent) {
    openaiPageViewSent = true;
    window.oaiq?.("measure", "page_viewed", { type: "contents" });
  }

  // The tag holds its queue (ttq.holdConsent()), so the page view is ours to send.
  if (ads) {
    window.ttq?.grantConsent();
    if (!tiktokPageViewSent) {
      tiktokPageViewSent = true;
      window.ttq?.page();
    }
  } else {
    window.ttq?.revokeConsent();
  }
}

CookieConsent.run({
  guiOptions: {
    consentModal: { layout: "box wide", position: "bottom right", flipButtons: false },
    preferencesModal: { layout: "box" },
  },
  categories: {
    necessary: { enabled: true, readOnly: true },
    analytics: {},
    ads: {},
  },
  onFirstConsent: applyConsent,
  onConsent: applyConsent,
  onChange: applyConsent,
  language: {
    default: "en",
    translations: {
      en: {
        consentModal: {
          // Two sentences, and the second one carries BOTH policy links. That is what
          // retired the `footer` key this used to set: the footer was a third band —
          // its own background, its own top border, its own row of links — repeating
          // the Cookie Policy link the description already had, and on a phone it
          // pushed the card past a third of the viewport. What the sentence dropped
          // was "Accept all, reject non-essential, or choose what to allow", which
          // only narrated the buttons sitting directly underneath it.
          title: "We value your privacy",
          description:
            "We use cookies to run the site, understand how it's used, and measure " +
            'our advertising. See our <a href="/privacy">Privacy Policy</a> and ' +
            '<a href="/cookies">Cookie Policy</a>.',
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          showPreferencesBtn: "Manage preferences",
        },
        preferencesModal: {
          title: "Cookie preferences",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          savePreferencesBtn: "Save preferences",
          closeIconLabel: "Close",
          sections: [
            {
              title: "Strictly necessary",
              description:
                "Required for the site to work — sign-in, security, and your consent choice. Always on.",
              linkedCategory: "necessary",
            },
            {
              title: "Analytics",
              description:
                "Help us understand how the site is used — including session replays " +
                "and heatmaps — so we can improve it.",
              linkedCategory: "analytics",
            },
            {
              title: "Advertising",
              description:
                "Let us measure our ad campaigns and show relevant ads on Google, Meta, TikTok and ChatGPT.",
              linkedCategory: "ads",
            },
            {
              title: "More information",
              description:
                'See our <a href="/cookies">Cookie Policy</a> and <a href="/privacy">Privacy Policy</a>.',
            },
          ],
        },
      },
    },
  },
});

// Used by the "open cookie settings" control in the Cookie Policy page.
window.openCookiePreferences = (): void => CookieConsent.showPreferences();

// …and wire that control here rather than from an inline onclick= in the ERB (CODE-2509)
document.addEventListener("click", (event) => {
  const trigger = (event.target as HTMLElement | null)?.closest("[data-cookie-preferences]");
  if (!trigger) return;
  event.preventDefault();
  CookieConsent.showPreferences();
});
